import { setInsuredAddress } from "@/common/addresses";
import { parseApiAnswer } from "@/common/answers";
import { populateClaimTable, showNoClaims } from "@/common/claims";
import {
  hasAnyFactFindAnswers,
  populateFactFindFromApi,
} from "@/common/factfind";
import { findAnswer, isError } from "@/common/quotes";
import {
  checkAndUpdateVisibility,
  hasRuleRequired,
  updateVisibilityBasedOn,
} from "@/common/visibility";
import { PRODUCT } from "@/constants/common.constants";
import { FACT_FIND_FORM } from "@/constants/factfind.constants";
import { LL_COVER_DETAILS_AND_ELIGIBILITY_INTERFACE_KEYS } from "@/constants/form.constants";
import router from "@/router";
import store from "@/store/index";

export default {
  SET_COVER_ANSWERS(state, payload) {
    state.coverAnswers = payload;
  },
  INCREASE_REPEATING_QUESTION_SET_INDEX(state) {
    state.form.repeatingQuestionSetIndex++;
  },
  SET_OCCUPATIONS(state, responseDataOccupations) {
    state.form.occupations = responseDataOccupations;
  },
  SET_INDUSTRIES(state, responseDataIndustries) {
    state.form.industries = responseDataIndustries;
  },
  SET_PRODUCTS(state, data) {
    state.products = data;
  },
  SET_CAPABILITIES(state, capabilities) {
    state.capabilities = capabilities;
  },
  SET_LEXIS_NEXIS_RESPONSE(state, responseLexisNexis) {
    state.lexisNexisResponse = responseLexisNexis;
  },
  SET_MAX_COMMISSION_SACRIFICE(state, { branchNumber, productsData }) {
    const webProductGroupId = state.quoteResponse.ProductId;
    const webProductObjects = productsData.filter(
      (wp) => wp.WebProductGroupId == webProductGroupId
    );
    const branchesForProduct = webProductObjects.flatMap((wp) => wp.Branches);
    const branchObject = branchesForProduct.find(
      (b) => b.BranchNo === branchNumber
    );
    state.maxCommissionSacrifice = branchObject.MaxSacrificeLevel;
  },
  SET_PROPERTY_DETAILS_FORM_VISIBILITY(state, payload) {
    state.form.propertyDetailsFormVisibility = payload;
  },
  SET_IS_LNERROR_MESSAGE_ENABLED(state, payload) {
    state.form.isLNErrorMessageEnabled = payload;
  },
  EXIT_TO_ADVISERHUB(_, destination = "current_quotes") {
    window.onbeforeunload = null;
    const ahUrl = process.env.VUE_APP_PSL_AH_URL + `#/${destination}`;
    window.location.href = ahUrl;
  },
  EXIT_TO_D2C_HOMEPAGE() {
    window.onbeforeunload = null;
    // use window location instead of router.push() to make page reload to clear vuex store and reload trust pilot
    window.location.href = router.resolve("/home").href;
  },
  EXIT_TO_D2C_LANDLORD_WELCOME_PAGE() {
    window.onbeforeunload = null;
    // use window location instead of router.push() to make page reload to clear vuex store and reload trust pilot
    window.location.href = router.resolve("/d2c/landlords/welcome").href;
  },
  SET_SECTIONS(state, responseDataForm) {
    state.form.sections = responseDataForm.Sections;
  },
  SET_QUESTION_VISIBILITY(_, { getters, question }) {
    // The value of `question` has changed
    // Find dependent questions and update their visibility and other stuff
    getters.allQuestions.find((item) => {
      if (
        item.RelatedQuestionId &&
        item.RelatedQuestionId.includes(question.Id)
      ) {
        updateVisibilityBasedOn(item, question, getters.allQuestions);
        // if the question is visible
        // then that means the condition has passed, we don't reset data
        if (item.IsVisible === false) {
          // for Repeating Questions only the Value property will return an array
          // here we want to clear any old values after showing/hiding hidden questions
          // with the exception of NCD which we want to retain when the user changes cover type
          if (
            item.InterfaceKey !== "NoClaimsBuildings" &&
            item.InterfaceKey !== "NoClaimsContents"
          ) {
            if (!Array.isArray(item.Value)) {
              item.Value = "";
            } else {
              item.Value = [];
              if (Object.hasOwn(item, "isClaimFormInvalid")) {
                item.isClaimFormInvalid = false;
              }
            }
          }

          // if a question has a fields array (applies to Repeating Questions)
          // loop through it and clear each Value property
          if (item.Fields) {
            item.Fields.forEach((input) => {
              input.Value = "";
            });
          }
        }
      }
    });
  },
  UPDATE_CREDENTIALS(state, data) {
    state.token = data.Token;
    state.userId = data.UserId;
    state.quoteAccessId = data.QuoteAccessId;
    if (data.QuoteAccessId) {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("userId");
      sessionStorage.setItem("quoteAccessId", data.QuoteAccessId);
    } else if (data.UserId && data.Token) {
      sessionStorage.setItem("token", data.Token);
      sessionStorage.setItem("userId", data.UserId);
      sessionStorage.removeItem("quoteAccessId");
    }
  },
  UPDATE_QUOTEACCESSID(state, quoteAccessId) {
    state.quoteAccessId = quoteAccessId;
    sessionStorage.setItem("quoteAccessId", quoteAccessId);
  },
  UPDATE_BRANCH(state, data) {
    state.branchNumber = data;
  },
  UPDATE_CALLCHANNEL(state, data) {
    state.callChannel = data;
  },
  UPDATE_QSID(state, data) {
    state.qsId = data;
  },
  UPDATE_PRODUCT_ID(state, data) {
    state.productId = data;
  },
  UPDATE_QUOTERESPONSE(state, data) {
    state.quoteResponse = data;
  },
  SET_QUOTE_STATUS(state, data) {
    state.quoteStatus = data;
    if (isError(data)) {
      state.lastQuoteErrorType = data;
    }
  },
  RESET_LAST_QUOTE_ERROR(state) {
    state.lastQuoteErrorType = null;
  },
  SET_USER_REFERENCE(state, data) {
    state.userReference = data;
  },
  UPDATE_COMMISSION_SACRIFICE(state, data) {
    state.commissionSacrifice = data;
  },
  SET_COVER_SELECTION(state, { buildingsAd, contentsAd }) {
    state.buildingsAd = buildingsAd;
    state.contentsAd = contentsAd;
  },
  LOAD_RETRIEVED_QUOTE_DATA(state, { data, getters }) {
    const coverType = data.find(
      (answer) => answer.InterfaceKey === "CoverType"
    )?.Value;

    // Expired LL quotes that are being requoted require a clean slate on the Quote Results screen,
    // so filter out any cover details and eligibility answers.
    if (getters.isExpired && getters.productId == PRODUCT.LandLord) {
      data = data.filter(
        (answer) =>
          !LL_COVER_DETAILS_AND_ELIGIBILITY_INTERFACE_KEYS.includes(
            answer.InterfaceKey
          )
      );
    }

    let hasInsuredAddress = false;
    state.lastEditedSectionTitle = null;
    // tracking the last section and it's title
    let lastSectionNumber = Number.MIN_SAFE_INTEGER;
    let lastSectionWithMatchingAnswerTitle = null;

    for (const section of state.form.sections) {
      if (!section.Questions) continue;
      for (const sectionQuestion of section.Questions) {
        //Show the questions related to cover type such as no claims discount.
        const foundCondition = sectionQuestion.Conditions?.find(
          (condition) => condition === coverType
        );
        if (foundCondition || coverType === null)
          sectionQuestion.IsVisible = true;

        showNoClaims(sectionQuestion, data);
        if (sectionQuestion.Questions) {
          for (const innerQuestion of sectionQuestion.Questions) {
            hasInsuredAddress ||= setInsuredAddress(innerQuestion, data);
            state.form.isLNErrorMessageEnabled = false;
            const matchingQuestion = findAnswer(data, innerQuestion);
            if (!matchingQuestion) continue;
            const sectionNumber = parseInt(section.SectionNumber);
            if (lastSectionNumber < sectionNumber) {
              lastSectionNumber = sectionNumber;
              lastSectionWithMatchingAnswerTitle = section.SectionTitle;
            }

            innerQuestion.Value = matchingQuestion.Value;
            innerQuestion.Source = matchingQuestion.Source;
          }
        }

        const isVisible = checkAndUpdateVisibility(
          sectionQuestion,
          getters.allQuestions,
          data
        );
        const matchingQuestion = findAnswer(data, sectionQuestion);

        if (!matchingQuestion) {
          const isMandatory = isVisible
            ? hasRuleRequired(sectionQuestion)
            : false;
          if (isMandatory && state.lastEditedSectionTitle === null) {
            // Any mandatory answer missed? Set the lastEditedSectionTitle and don't do this check any more
            // Special case for CoverType needs a falsy but not null value in lastEditedSectionTitle
            state.lastEditedSectionTitle =
              sectionQuestion.InterfaceKey === "CoverType"
                ? ""
                : section.SectionTitle;
          }
          continue;
        }
        const sectionNumber = parseInt(section.SectionNumber);
        if (lastSectionNumber < sectionNumber) {
          lastSectionNumber = sectionNumber;
          lastSectionWithMatchingAnswerTitle = section.SectionTitle;
        }

        if (matchingQuestion.Source !== "Default") {
          sectionQuestion.Value = parseApiAnswer(matchingQuestion);
        } else {
          console.log("not setting", matchingQuestion);
        }
        //sectionQuestion.Value = parseApiAnswer(matchingQuestion);
        sectionQuestion.Source = matchingQuestion.Source;
      }
    }

    // if we find section with matching answer and we can't find lastEditedSectionTitle
    // => this quote has all the data in place and should be in the last section
    if (
      state.lastEditedSectionTitle == null &&
      lastSectionWithMatchingAnswerTitle
    ) {
      state.lastEditedSectionTitle = lastSectionWithMatchingAnswerTitle;
    }

    populateClaimTable(state, data);

    if (hasAnyFactFindAnswers(data)) {
      const factFindFormImport = FACT_FIND_FORM;
      populateFactFindFromApi(data, factFindFormImport);
      state.factFind = factFindFormImport;
    }

    // When we resume, we show prop details in form, not card
    // and only if there was a valid insured address
    state.form.propertyDetailsFormVisibility = hasInsuredAddress;
  },
  CACHE_FORM_ORIGINAL_ANSWERS(state, data) {
    state.cachedOriginalFormAnswers = data;
  },
  SET_IS_DRAWER_CUSTOMISE_COVER_POPULTED_FROM_API(state, data) {
    state.isDrawerCustomiseCoverPopulatedFromApi = data;
  },
  SET_NCD_BUILDINGS_STATE(state, data) {
    state.ncdBuildingsState = data;
  },
  SET_NCD_CONTENTS_STATE(state, data) {
    state.ncdContentsState = data;
  },
  SET_LAST_STEP_EDITED(_, data) {
    store.commit("steps/SET_LAST_STEP_EDITED", data, {
      root: true,
    });
  },
  SET_OVERLAY_VISIBLE(state, data) {
    state.overlay = data;
  },
  SUBMIT_FACT_FIND(state, data) {
    state.factFind = data;
  },
  SET_IS_EXPIRED_FLAG(state, data) {
    state.isExpired = data;
  },
  SET_CONTACT_INFO(state, data) {
    state.contactInfo = JSON.parse(data);
  },
  SET_PRODUCT_TRANSFER_IN_PROGRESS_FLAG(state, data) {
    state.productTransferInProgress = data;
  },
  SET_QUICK_QUOTE(state, data) {
    state.isQuickQuote = data;
  },
  SET_REFRESH_QUOTE(state, data) {
    state.needRefreshQuote = data;
  },
  SET_HOME_ELIGIBILITY(state, data) {
    if (typeof data === "object" && !Array.isArray(data) && data !== null) {
      if (data.ProductId != 1008 && data.ProductId != 1015) {
        return;
      }
      const homeEligibilityObj = data.Answers.find(
        (answer) => answer.InterfaceKey === "HomeEligibility"
      );
      const homeEligibilityIndicativeQuote = JSON.parse(
        sessionStorage.getItem("homeEligibilityIndicativeQuote")
      );
      if (homeEligibilityObj) {
        state.homeEligibility = [homeEligibilityObj];
      }
      if (data.IsQuickQuote && !homeEligibilityIndicativeQuote) {
        sessionStorage.setItem(
          "homeEligibilityIndicativeQuote",
          JSON.stringify(homeEligibilityObj)
        );
      }
    } else {
      state.homeEligibility[0].Value = data;
      const homeEligibilityIndicativeQuote = {
        Value: data,
        InterfaceKey: "HomeEligibility",
        Source: "User",
      };
      sessionStorage.setItem(
        "homeEligibilityIndicativeQuote",
        JSON.stringify(homeEligibilityIndicativeQuote)
      );
    }
  },
  SET_IS_ANNUAL_REQUOTE(state, data) {
    state.isAnnualRequote = data;
  },
  SET_IS_QUOTE_REQUEST_ID_AFTER_RETRIEVE(state, data) {
    state.quoteRequestIdAfterRetrieve = data;
  },
  SET_SELECTED_QUOTE_IDS(state, data) {
    state.selectedQuoteIds = data;
  },
  UPDATE_CLIENT_ERROR_RESPONSE(state, data) {
    state.lastClientErrorResponse = data;
  },
  TOGGLE_SAVE_AND_EXIT_DIALOG(state) {
    state.isShowSaveAndExitDialog = !state.isShowSaveAndExitDialog;
  },
  TOGGLE_GLOBAL_SAVE_AND_EXIT_DIALOG(state) {
    state.isShowGlobalSaveAndExitDialog = !state.isShowGlobalSaveAndExitDialog;
  },
  SET_SHOW_APP_OVERLAY(state, data) {
    state.showAppOverlay = data;
  },
  SET_QUESTION_MAP(state, data) {
    state.questionMap = data;
  },
  SET_RESTORING_LAST_STEP(state, data) {
    state.restoringLastStep = data;
  },
  TOGGLE_GA_EVENT_FLAG(state) {
    state.isGA4EventToBeFired = !state.isGA4EventToBeFired;
  },
  HAS_RESET_QUICKQUOTE_PROPERTYDATA(state, value) {
    state.hasResetQuickQuotePropertyData = value;
  },
  RESET_INVALID_QUESTIONS(state, { errors }) {
    Object.entries(errors).forEach(([key, value]) => {
      if (value && value.length) {
        if (state.questionMap[key]) {
          state.questionMap[key].Value = null;
        }
      }
    });
  },
  SET_QUOTE_EXCESS_CONFIGURATION(state, data) {
    state.quoteExcessConfiguration = JSON.parse(data);
  },
  RESET_FACT_FIND_SECOND_APPLICANT(state, value) {
    for (const key in state.factFind) {
      if (key.includes(value)) {
        delete state.factFind[key];
      }
    }
  },
  SET_CURRENT_REF_OBSERVER_FORM(state, data) {
    state.currentRefObserverForm = data;
  },
  SET_EXIT_DESTINATION(state, data) {
    state.exitDestination = data;
  },
  SET_CURRENT_PATH(state, data) {
    state.currentPath = data;
  },
};
